import React from "react";
import styles from "./CodeField.module.scss";

export interface CodeFieldProps {
  length?: number;
  onCodeEntered?: (code: string) => void;
  value?: string;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
  errorMessage?: string;
  nodetail?: boolean;
}

const CodeField = React.forwardRef<HTMLInputElement, CodeFieldProps>(
  (
    {
      length = 5,
      onCodeEntered,
      onChange,
      value,
      autoFocus,
      errorMessage,
      nodetail,
    },
    ref,
  ) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    const focusInput = (index: number) => {
      if (index >= 0 && index < length) {
        inputRefs.current[index]?.focus();
        setTimeout(() => inputRefs.current[index]?.select(), 0);
      }
    };

    const handleChange = (index: number, val: string) => {
      const digits = val.replace(/\D/g, "").split("");

      // If multiple digits are pasted/inserted (from Samsung keyboard or otherwise)
      if (digits.length > 1) {
        digits.slice(0, length).forEach((char, i) => {
          if (inputRefs.current[i]) {
            inputRefs.current[i]!.value = char;
          }
        });

        const fullValue = digits.slice(0, length).join("");
        onChange?.(fullValue);
        if (fullValue.length === length) {
          onCodeEntered?.(fullValue);
        }
        focusInput(Math.min(digits.length, length - 1));
        return;
      }

      // Normal single-digit typing
      const digit = digits[0] || "";
      inputRefs.current[index]!.value = digit;

      const currentValue = inputRefs.current
        .map((i) => i?.value || "")
        .join("");
      onChange?.(currentValue);

      if (digit && index < length - 1) {
        focusInput(index + 1);
      } else if (currentValue.length === length) {
        onCodeEntered?.(currentValue);
      }
    };

    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
      index: number,
    ) => {
      const key = e.key;

      // Allow Tab
      if (key === "Tab") return;

      // Allow Ctrl/Cmd + V for pasting
      if ((e.ctrlKey || e.metaKey) && key.toLowerCase() === "v") {
        return; // Let native paste proceed
      }

      // Backspace clears current and moves focus back
      if (key === "Backspace") {
        e.preventDefault();
        if (inputRefs.current[index]) {
          inputRefs.current[index]!.value = "";
        }

        const currentValue = inputRefs.current
          .map((i) => i?.value || "")
          .join("");
        onChange?.(currentValue);

        if (index > 0) focusInput(index - 1);
        return;
      }

      // Arrow navigation
      if (key === "ArrowLeft") {
        e.preventDefault();
        focusInput(index - 1);
        return;
      }

      if (key === "ArrowRight") {
        e.preventDefault();
        focusInput(index + 1);
        return;
      }

      // Block non-digits
      if (!/^\d$/.test(key)) {
        e.preventDefault();
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .split("");

      if (!pastedText) return;
      if (pastedText.length > 1) {
        pastedText.slice(0, length).forEach((char, i) => {
          if (inputRefs.current[i]) {
            inputRefs.current[i]!.value = char;
          }
        });

        const fullValue = pastedText.slice(0, length).join("");
        onChange?.(fullValue);
        if (fullValue.length === length) {
          onCodeEntered?.(fullValue);
        }
        focusInput(Math.min(pastedText.length, length - 1));
        return;
      }
    };

    const inputs = Array.from({ length }).map((_, i) => (
      <input
        key={i}
        ref={(el) => {
          inputRefs.current[i] = el;
        }}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d*"
        maxLength={5}
        className={styles["code-input__inputs__input"]}
        onChange={(e) => handleChange(i, e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, i)}
        onPaste={handlePaste}
        onFocus={(e) => e.target.select()}
        autoFocus={i === 0 ? autoFocus : false}
      />
    ));

    return (
      <div className={styles["code-input"]}>
        <input
          type="hidden"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={value}
          ref={ref}
        />
        <div className={styles["code-input__inputs"]}>{inputs}</div>
        {!nodetail && errorMessage && (
          <p className={styles["code-input__message"]}>{errorMessage}</p>
        )}
      </div>
    );
  },
);

CodeField.displayName = "CodeField";

export default CodeField;
